import { State } from '@meraki-internal/state';

export interface IOfflineRecordingItem {
    id: string;
    action: () => Promise<void>;
    timestamp: string;
    retryCount: number;
}

export class OfflineRecordingQueue extends State<{}> {

    private savingPromises: {
        [recordingId: string]: Promise<void> | undefined;
    } = {};

    private savingQueue: {
        [recordingId: string]: { action: () => Promise<void> } | undefined
    } = {};

    private savingError: {
        [key: string]: Error | undefined
    } = {};

    isSaving = ({ recordingId }: { recordingId: string; }) => Boolean(this.savingPromises[recordingId]);

    getError = ({ recordingId }: { recordingId: string; }) => this.savingError[recordingId];

    clearError = ({ recordingId, error }: { recordingId: string; error: Error; }) => {
        if (this.savingError[recordingId] === error){
            this.savingError[recordingId] = undefined;
        }
    };

    enqueue = ({ recordingId, action }: { recordingId: string, action: () => Promise<void> }) => {
        this.savingQueue[recordingId] = { action };
        this.tryDequeue({ recordingId });
    };

    private tryDequeue = ({ recordingId }: { recordingId: string;}) => {
        if (this.savingPromises[recordingId]){
            return;
        }
        if (!this.savingQueue[recordingId]){
            return;
        }
        this.savingPromises[recordingId] = Promise.resolve()
            .then(async () => {
                this.savingError[recordingId] = undefined;

                // dequeue it
                const { action } = this.savingQueue[recordingId]!;
                this.savingQueue[recordingId] = undefined;

                // save it
                await action();
            })
            .catch(err => {
                this.savingError[recordingId] = err;
            })
            .finally(() => {
                this.savingPromises[recordingId] = undefined;
                this.tryDequeue({ recordingId });

                // we may be done saving
                this.setState({});
            });
        // we're saving
        this.setState({});
    };

    // Get count of pending items
    getPendingCount = (): number => {
        return Object.keys(this.savingQueue).length + Object.keys(this.savingPromises).filter(key => this.savingPromises[key]).length;
    };

    // Check if any recordings are pending
    hasPending = (): boolean => {
        return this.getPendingCount() > 0;
    };

    // Cleanup old errors
    cleanup = () => {
        const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

        for (const [recordingId, error] of Object.entries(this.savingError)) {
            if (error && Date.now() - oneWeekAgo > 0) {
                delete this.savingError[recordingId];
            }
        }
    };
}
